import React, {useState, useEffect} from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {useMutation, useQuery, useLazyQuery} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router-dom';
import {testAccountDetails} from '../../../tests/e2e/e2eCuroTestAccounts';
import {OpenInCuro} from '../../utils/getCuroURL';
import {useGodMode} from './DebugModal';
import {getBusinessUnitsQuery, useDjangoContext} from '../../contexts/useAuthContext';
import {
  SwitchBusinessUnit,
  SwitchBusinessUnitVariables,
} from '../../schema-types/SwitchBusinessUnit';
import {GetBusinessUnits} from '../../schema-types/GetBusinessUnits';
import {GET_LOGIN_INFO_QUERY} from '../../hooks/useDjango';
import {switchBusinessUnitMutation} from '../Admin/AdminDialog';
import {PermGroupsEnum, OneViewModelTypesEnum} from '../../schema-types/globalTypes';
import {
  SingleAccountBasic,
  SingleAccountBasicVariables,
} from '../../schema-types/SingleAccountBasic';
import {
  GetNativeIdFromCuro,
  GetNativeIdFromCuroVariables,
} from '../../schema-types/GetNativeIdFromCuro';
import {
  GET_ACCOUNT_BASIC,
  GET_ACCOUNT_FULL,
} from '../AccountOverview/accountOverviewQueries';
import {GET_NATIVE_ID_FROM_CURO} from '../../pages/TestSetupPages/TestHomepage';
import {AccountFull, AccountFullVariables} from '../../schema-types/AccountFull';
import {useDebugContext} from '../../contexts/DebugProvider';
import validateUUID from '../../utils/validateUUID';
import {AddAccountDummyDataButton} from '../AccountOverview/AddAccountDummyDataButton';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

interface AccountActionsModalProps {
  name: string;
  account: testAccountDetails;
  onClose: () => void;
  onCloseAllModals?: () => void;
}

export const AccountActionsModal: React.FC<AccountActionsModalProps> = ({
  name,
  account,
  onClose,
  onCloseAllModals,
}) => {
  const navigate = useNavigate();
  const {djangoUser} = useDjangoContext();
  const {setDebug} = useDebugContext();
  const {onSetGroup} = useGodMode();
  const {enqueueSnackbar} = useSnackbar();
  const [showPadGroupSelector, setShowPadGroupSelector] = useState(false);
  const [nativeAccountId, setNativeAccountId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // First, get the native ID from the Curo ID
  const [onGetNative] = useLazyQuery<GetNativeIdFromCuro, GetNativeIdFromCuroVariables>(
    GET_NATIVE_ID_FROM_CURO
  );

  // Fetch full account data with contacts
  const {data: accountFullData} = useQuery<AccountFull, AccountFullVariables>(
    GET_ACCOUNT_FULL,
    {
      variables: {accountId: validateUUID(nativeAccountId)!},
      skip: !nativeAccountId,
    }
  );

  const accountData = accountFullData?.accountDetails;

  // Effect to get native ID when modal is opened
  useEffect(() => {
    const curoId = validateUUID(account.curoId);
    if (curoId && !nativeAccountId) {
      onGetNative({
        variables: {
          curoId,
          modelName: OneViewModelTypesEnum.ACCOUNT,
        },
      }).then(({data}) => {
        if (data?.nativeId) {
          setNativeAccountId(data.nativeId);
        }
      });
    }
  }, [account.curoId, nativeAccountId, onGetNative]);

  // Get business unit from fetched account data
  const businessUnit = accountData?.businessUnit;
  const [_switchBusinessUnit] = useMutation<
    SwitchBusinessUnit,
    SwitchBusinessUnitVariables
  >(switchBusinessUnitMutation);
  const {data: _businessUnitsData} = useQuery<GetBusinessUnits>(getBusinessUnitsQuery);

  // Map account type to permission group
  const getPermissionGroupFromAccountType = (accountType: string): PermGroupsEnum => {
    switch (accountType) {
      case 'sfp':
        return PermGroupsEnum.sfp;
      case 'spp da':
        return PermGroupsEnum.spp_da;
      case 'spp ar':
        return PermGroupsEnum.spp_ar;
      default:
        return PermGroupsEnum.sfp; // Default fallback
    }
  };

  // Function to set up the environment for a specific account
  const setupForAccount = async (businessUnitId: string, skipRefresh = false) => {
    setIsLoading(true);
    try {
      // Check if account name contains "PAD" or "pad"
      const accountName = `${name} ${account.primarySimplifiedName} ${
        account.secondaryName || ''
      }`;
      const isPadAccount = accountName.toLowerCase().includes('pad');

      // first set the group to god to switch business unit
      try {
        await onSetGroup(PermGroupsEnum.god);
      } catch (error) {
        enqueueSnackbar('Failed to set god permissions', {variant: 'error'});
        throw error;
      }

      setDebug((x) => ({...x, forms: true}));

      // Set business unit
      try {
        await _switchBusinessUnit({
          variables: {
            staffId: djangoUser.id,
            businessUnitId,
          },
          refetchQueries: skipRefresh ? [] : [GET_LOGIN_INFO_QUERY],
        });
        enqueueSnackbar('Business unit switched successfully', {variant: 'success'});
      } catch (error) {
        enqueueSnackbar('Failed to switch business unit', {variant: 'error'});
        throw error;
      }

      // If it's a PAD account, show the group selector dialog
      if (isPadAccount) {
        setShowPadGroupSelector(true);
      } else {
        // Set user group based on account type
        const group = getPermissionGroupFromAccountType(account.type);
        try {
          await onSetGroup(group);
          enqueueSnackbar(`Permission group set to ${group}`, {variant: 'success'});
        } catch (error) {
          enqueueSnackbar('Failed to set permission group', {variant: 'error'});
          throw error;
        }
      }
    } catch (error) {
      console.error('Setup for account failed:', error);
      // Don't rethrow here to prevent navigation
      return false;
    } finally {
      setIsLoading(false);
    }
    return true;
  };

  // Handle PAD group selection
  const handlePadGroupSelection = async (selectedGroup: PermGroupsEnum) => {
    setIsLoading(true);
    try {
      await onSetGroup(selectedGroup);
      enqueueSnackbar(`Permission group set to ${selectedGroup}`, {variant: 'success'});
      setShowPadGroupSelector(false);

      // Close the main modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      enqueueSnackbar('Failed to set permission group', {variant: 'error'});
    } finally {
      setIsLoading(false);
    }
  };

  // Handle button clicks with setup
  const handleButtonClick = async (url: string) => {
    const businessUnitId = accountData?.businessUnit?.id;
    if (!businessUnitId) {
      enqueueSnackbar('No business unit found for this account type', {variant: 'error'});
      return;
    }

    const success = await setupForAccount(businessUnitId);
    if (success) {
      // Close all modals before navigating
      onCloseAllModals?.();
      navigate(url);
    }
  };

  // Get PAD-related permission groups
  const getPadPermissionGroups = (): PermGroupsEnum[] => {
    return [
      PermGroupsEnum.pad_junior,
      PermGroupsEnum.pad_relationship_manager,
      PermGroupsEnum.pad_senior,
      PermGroupsEnum.sfp,
    ];
  };

  const disabled = !accountData?.businessUnit?.id || isLoading;
  const {recommendsGridUrl, createRecommendUrl} = useRequirementsDentistOrBAU();

  return (
    <>
      <Box>
        <Typography variant="h5" fontWeight="bold" mb={1}>
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {account.primarySimplifiedName} {account.secondaryName || ''}
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={2}>
          {account.type.toUpperCase()}
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Business Unit: {businessUnit?.name || 'Not specified'}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          mb={2}
          sx={{fontStyle: 'italic'}}
        >
          Note: clicking any button below will set you up with the right permissions and
          business unit
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <Divider />

          <Button
            variant="outlined"
            size="large"
            fullWidth
            disabled={disabled}
            onClick={() => handleButtonClick(`/account-redirect/${account.curoId}`)}
          >
            {isLoading ? 'Loading...' : 'Open account'}
          </Button>

          {account.type === 'spp da' ? null : (
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={disabled}
              onClick={() =>
                handleButtonClick(`/test/test-new-review/${account.curoId}?goTo=meeting`)
              }
            >
              {isLoading ? 'Loading...' : 'Create Review'}
            </Button>
          )}

          {account.type === 'spp da' ? (
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={disabled}
              onClick={() =>
                handleButtonClick(
                  `/test/test-instruction/${account.curoId}?goTo=${createRecommendUrl}`
                )
              }
            >
              {isLoading ? 'Loading...' : 'Create Instruction'}
            </Button>
          ) : null}

          {account.type === 'spp da' ? (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={disabled}
              onClick={() =>
                handleButtonClick(
                  `/account-redirect/${account.curoId}/${recommendsGridUrl}`
                )
              }
            >
              {isLoading ? 'Loading...' : 'View Instructions'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={disabled}
              onClick={() =>
                handleButtonClick(`/account-redirect/${account.curoId}/reviews`)
              }
            >
              {isLoading ? 'Loading...' : 'View Reviews'}
            </Button>
          )}

          {account.type !== 'spp da' ? (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={disabled}
              onClick={() =>
                handleButtonClick(
                  `/test/test-any-open-review/${account.curoId}?goTo=${createRecommendUrl}`
                )
              }
            >
              {isLoading ? 'Loading...' : 'Create Recommend'}
            </Button>
          ) : null}

          {account.type === 'spp da' ? null : (
            <Button
              variant="outlined"
              size="large"
              fullWidth
              disabled={disabled}
              onClick={() =>
                handleButtonClick(`/test/test-any-open-review/${account.curoId}`)
              }
            >
              {isLoading ? 'Loading...' : 'Any Review'}
            </Button>
          )}

          <OpenInCuro
            curoType="account"
            curoId={account.curoId}
            alwaysShow
            disabled={disabled}
          >
            <Button variant="outlined" size="large" fullWidth onClick={onClose}>
              Open account in Curo
            </Button>
          </OpenInCuro>

          <AddAccountDummyDataButton account={accountFullData?.accountDetails} />
        </Box>
      </Box>

      {/* PAD Group Selection Dialog */}
      <Dialog
        open={showPadGroupSelector}
        onClose={isLoading ? undefined : () => setShowPadGroupSelector(false)}
      >
        <DialogTitle>Select Permission Group</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            This appears to be a PAD account. Please select the appropriate permission
            group:
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {getPadPermissionGroups().map((group) => (
              <Button
                key={group}
                variant="outlined"
                onClick={() => handlePadGroupSelection(group)}
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? 'Loading...' : group.replace(/_/g, ' ').toUpperCase()}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPadGroupSelector(false)} disabled={isLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
